import React, { useState, useEffect, useRef } from 'react';
import { venuesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import { Image, FileText, Upload, Trash2, Loader2, Download, Pencil, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const LocationFiles = ({ locationId }) => {
  const { isViewer } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [description, setDescription] = useState('');
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, file: null });
  const [editingId, setEditingId] = useState(null);
  const [editDescription, setEditDescription] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadFiles();
  }, [locationId]);

  const loadFiles = async () => {
    try {
      const res = await venuesAPI.listFiles(locationId);
      setFiles(res.data);
    } catch (error) {
      console.error('Failed to load files:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    setUploading(true);
    try {
      const { default: axios } = await import('axios');
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(
          `${apiUrl}/api/locations/${locationId}/files?description=${encodeURIComponent(description)}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${token}`,
            },
          }
        );
      }
      toast.success(`${selectedFiles.length} file(s) uploaded`);
      setShowUpload(false);
      setDescription('');
      setSelectedFiles(null);
      loadFiles();
    } catch (error) {
      toast.error('Failed to upload file(s)');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId) => {
    try {
      await venuesAPI.deleteFile(fileId);
      toast.success('File deleted');
      setDeleteConfirm({ open: false, file: null });
      loadFiles();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const handleUpdateDescription = async (fileId) => {
    try {
      await venuesAPI.updateFileDescription(fileId, editDescription);
      setEditingId(null);
      loadFiles();
      toast.success('Description updated');
    } catch (error) {
      toast.error('Failed to update description');
    }
  };

  const openPreview = async (file) => {
    if (!file.content_type?.startsWith('image/')) {
      // For non-images, download directly
      downloadFile(file);
      return;
    }
    try {
      const res = await venuesAPI.downloadFile(file.id);
      const url = URL.createObjectURL(res.data);
      setPreviewUrl(url);
      setPreviewFile(file);
    } catch (error) {
      toast.error('Failed to load preview');
    }
  };

  const downloadFile = async (file) => {
    try {
      const res = await venuesAPI.downloadFile(file.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewFile(null);
  };

  const isImage = (contentType) => contentType?.startsWith('image/');

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Generate thumbnail URLs on mount for image files
  const [thumbnails, setThumbnails] = useState({});

  useEffect(() => {
    const loadThumbnails = async () => {
      const newThumbnails = {};
      for (const file of files) {
        if (isImage(file.content_type) && !thumbnails[file.id]) {
          try {
            const res = await venuesAPI.downloadFile(file.id);
            newThumbnails[file.id] = URL.createObjectURL(res.data);
          } catch {
            // skip
          }
        }
      }
      if (Object.keys(newThumbnails).length > 0) {
        setThumbnails(prev => ({ ...prev, ...newThumbnails }));
      }
    };
    if (files.length > 0) loadThumbnails();
    return () => {
      Object.values(thumbnails).forEach(url => URL.revokeObjectURL(url));
    };
  }, [files]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-border/50 bg-card/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Image className="h-5 w-5 text-amber-400" />
              Files & Photos
              {files.length > 0 && (
                <Badge variant="outline" className="ml-2">{files.length}</Badge>
              )}
            </CardTitle>
            {!isViewer && (
              <Button
                onClick={() => setShowUpload(true)}
                size="sm"
                data-testid="upload-file-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {files.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {files.map((file) => (
                <div
                  key={file.id}
                  className="group border border-border/50 rounded-lg overflow-hidden bg-background/50 hover:border-border transition-colors"
                >
                  {/* Thumbnail / Icon */}
                  <div
                    className="aspect-[4/3] bg-muted/30 flex items-center justify-center cursor-pointer relative overflow-hidden"
                    onClick={() => openPreview(file)}
                    data-testid={`file-preview-${file.id}`}
                  >
                    {isImage(file.content_type) && thumbnails[file.id] ? (
                      <img
                        src={thumbnails[file.id]}
                        alt={file.description || file.original_filename}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <FileText className="h-12 w-12 text-muted-foreground/50" />
                    )}
                    {!isViewer && (
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                        <Button
                          variant="secondary"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => { e.stopPropagation(); downloadFile(file); }}
                          data-testid={`download-file-${file.id}`}
                        >
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ open: true, file }); }}
                          data-testid={`delete-file-${file.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                    {isViewer && (
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="secondary"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => { e.stopPropagation(); downloadFile(file); }}
                        >
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                  {/* Info */}
                  <div className="p-3 space-y-1">
                    {editingId === file.id ? (
                      <div className="flex gap-2">
                        <Input
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          className="h-7 text-xs"
                          placeholder="Description..."
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleUpdateDescription(file.id);
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                        />
                        <Button size="icon" className="h-7 w-7 shrink-0" onClick={() => handleUpdateDescription(file.id)}>
                          <span className="text-xs">OK</span>
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-1">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">
                            {file.description || file.original_filename}
                          </p>
                          {file.description && (
                            <p className="text-xs text-muted-foreground truncate">{file.original_filename}</p>
                          )}
                        </div>
                        {!isViewer && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => { setEditingId(file.id); setEditDescription(file.description || ''); }}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {formatSize(file.size)} {file.uploaded_by_name && `· ${file.uploaded_by_name}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Image className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p>No files uploaded yet</p>
              {!isViewer && (
                <p className="text-sm mt-1">Upload photos of rooms, loading docks, maps, and more</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Files</DialogTitle>
            <DialogDescription>
              Upload photos, maps, or documents for this location.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Files</Label>
              <Input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                data-testid="file-input"
              />
              {selectedFiles && (
                <p className="text-xs text-muted-foreground">{selectedFiles.length} file(s) selected</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. Loading dock view from parking lot, Ballroom A layout map..."
                rows={2}
                className="resize-none"
                data-testid="file-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
            <Button
              onClick={handleUpload}
              disabled={uploading || !selectedFiles?.length}
              data-testid="confirm-upload-btn"
            >
              {uploading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
              ) : (
                <><Upload className="h-4 w-4 mr-2" /> Upload</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog open={!!previewFile} onOpenChange={closePreview}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{previewFile?.description || previewFile?.original_filename}</DialogTitle>
          </DialogHeader>
          {previewUrl && (
            <div className="flex justify-center">
              <img
                src={previewUrl}
                alt={previewFile?.description || previewFile?.original_filename}
                className="max-h-[70vh] object-contain rounded-lg"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => previewFile && downloadFile(previewFile)}>
              <Download className="h-4 w-4 mr-2" /> Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => { if (!open) setDeleteConfirm({ open: false, file: null }); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete File</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteConfirm.file?.description || deleteConfirm.file?.original_filename}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => handleDelete(deleteConfirm.file?.id)}
              data-testid="confirm-delete-file-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LocationFiles;
